import React, { useEffect } from "react";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { formatISO } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Toggle from "@/components/forms/Toggle";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BrushCleaning } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";

const ORIGIN_OPTIONS = [
  { label: "Campaña", value: "facebook" },
  { label: "WhatsApp", value: "Whatsapp_Button" },
  { label: "Llamada", value: "Call_Button" },
  { label: "PRO CRM", value: "Pro_Crm" },
  { label: "Formulario", value: "Formulario" },
];

const FiltersSchema = Yup.object().shape({
  search: Yup.string(),
  property: Yup.string(),
  status: Yup.string(),
  start_date: Yup.string(),
  end_date: Yup.string(),
  pipeline_stage: Yup.array().of(Yup.string()),
  origin: Yup.array().of(Yup.string()),
  property_id: Yup.string(),
});

const AutoSubmit = ({ values, submitForm }) => {
  useEffect(() => {
    submitForm(values);
  }, [values, submitForm]);

  return null;
};

AutoSubmit.propTypes = {
  values: PropTypes.object.isRequired,
  submitForm: PropTypes.func.isRequired,
};

export default function Filters({
  onSubmit,
  onReset,
  managedLeads,
  pipelines,
}) {
  const submit = (values) => {
    const v = { ...values };
    Object.keys(v).forEach((key) => {
      if (v[key] === "") {
        delete v[key];
      }
    });

    if (v.start_date && v.end_date) {
      v.created_range = `${formatISO(v.start_date.setHours(0, 0, 0), {
        representation: "complete",
      })},${formatISO(v.end_date.setHours(23, 59, 59), {
        representation: "complete",
      })}`;
    }
    if (v.origin && v.origin.length > 0) {
      const contactPlaces = [];
      const utmCampaigns = [];

      v.origin.forEach((val) => {
        if (val === "facebook") contactPlaces.push(val);
        else utmCampaigns.push(val);
      });

      if (contactPlaces.length > 0) v.contact_place = contactPlaces.join(",");
      if (utmCampaigns.length > 0) v.utm_campaign = utmCampaigns.join(",");
      delete v.origin;
    }

    v.page = 1;
    onSubmit(v);
  };

  return (
    <div>
      <Formik
        initialValues={{
          search: "",
          property: "",
          status: "1",
          start_date: "",
          end_date: "",
          pipeline_stage: [],
          origin: [],
          property_id: "",
        }}
        validationSchema={FiltersSchema}
        onSubmit={submit}
      >
        {({ values, resetForm, setFieldValue, submitForm }) => (
          <Form>
            <AutoSubmit values={values} submitForm={submitForm} />
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3 items-end">
              <div>
                <Field name="search">
                  {({ field }) => (
                    <Input
                      {...field}
                      placeholder="Nombre, email o teléfono"
                      className="w-full"
                    />
                  )}
                </Field>
                <ErrorMessage
                  component="span"
                  className="input-error"
                  name="search"
                />
              </div>

              <div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between"
                    >
                      {values.pipeline_stage.length > 0
                        ? `${values.pipeline_stage.length} seleccionados`
                        : "Estatus"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[200px] max-h-60 overflow-y-auto">
                    {pipelines?.[0]?.stages?.map((stage) => {
                      const stageId = String(stage.id);
                      const isChecked = values.pipeline_stage.includes(stageId);

                      return (
                        <DropdownMenuCheckboxItem
                          key={stageId}
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...values.pipeline_stage, stageId]
                              : values.pipeline_stage.filter(
                                  (id) => id !== stageId
                                );
                            setFieldValue("pipeline_stage", newValue);
                          }}
                        >
                          {stage.name}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between"
                    >
                      {values.origin.length > 0
                        ? `${values.origin.length} seleccionados`
                        : "Origen"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[200px] max-h-60 overflow-y-auto">
                    {ORIGIN_OPTIONS.map((option) => {
                      const isChecked = values.origin.includes(option.value);

                      return (
                        <DropdownMenuCheckboxItem
                          key={option.value}
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...values.origin, option.value]
                              : values.origin.filter(
                                  (val) => val !== option.value
                                );
                            setFieldValue("origin", newValue);
                          }}
                        >
                          {option.label}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div>
                <Field name="property_id">
                  {({ field, form }) => (
                    <Input
                      {...field}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="ID Propiedad"
                      className="w-full"
                      onChange={(e) => {
                        const value = e.target.value;
                        const numericValue = value.replace(/\D/g, "");
                        form.setFieldValue(field.name, numericValue);
                      }}
                    />
                  )}
                </Field>
              </div>

              <div className="relative">
                <DatePicker
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  placeholderText="Periodo de contactos"
                  selectsRange={true}
                  startDate={values.start_date}
                  endDate={values.end_date}
                  onChange={([start_date, end_date]) => {
                    setFieldValue("start_date", start_date);
                    setFieldValue("end_date", end_date);
                  }}
                />
              </div>

              {managedLeads && (
                <div className="flex items-center space-x-2 h-9">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Sólo perfilados
                  </span>
                  <Toggle
                    type="checkbox"
                    name="only_managed_leads"
                    className="toggle-input"
                    status={values.only_managed_leads}
                    onChange={(e) => {
                      setFieldValue("only_managed_leads", e);
                      setFieldValue("managed_leads", managedLeads);
                    }}
                  />
                </div>
              )}

              <div className="flex h-9">
                <Button
                  variant="outline"
                  type="button"
                  className="w-full"
                  onClick={() => {
                    resetForm();
                    onReset();
                  }}
                >
                  <BrushCleaning className="mr-2 h-4 w-4" />
                  Limpiar
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

Filters.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  managedLeads: PropTypes.bool,
};

Filters.defaultProps = {
  onSubmit: () => {},
  onReset: () => {},
};
